/**
 * State that survives reloads by persisting to localStorage.
 *
 * Like useState, but the value is read from localStorage on first mount and
 * written back whenever it changes. If nothing is stored (first visit, cleared
 * storage, or a private-mode failure) the `fallback` is used — so callers get a
 * sensible default without special-casing "not set yet".
 *
 * Keys are namespaced `specta.*` to match the rest of the app's storage.
 */
import { useCallback, useEffect, useState } from "react";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function usePersistentState<T>(
  key: string,
  fallback: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const storageKey = `specta.${key}`;

  // Lazy initialiser so we read storage once, not on every render.
  const [value, setValue] = useState<T>(() => read(storageKey, fallback));

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Storage full or unavailable (private mode) — keep working in memory.
    }
  }, [storageKey, value]);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue(next);
  }, []);

  return [value, set];
}
